import {
  Button,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useScheduledActivityMessagesContext } from '../../hooks/useScheduledActivityMessagesContext';
import { ScheduledActivityMessage } from '../../models/ScheduledActivityMessage';
import { UserHelper } from '../../models/UserHelper';
import useStyles from '../../styles/useStyles';
import { tsFormatPastOnly } from '../../util/dateUtil';
import { StyledMarkdown } from '../Global/StyledMarkdown';
import UserAvatar from '../User/UserAvatar';
import ScheduledActivityMessageForm from './ScheduledActivityMessageForm';

interface Props {
  message: ScheduledActivityMessage
  isChild?: boolean
  disableReply?: boolean
}

export default function ScheduledActivityMessageDetail({
  message,
  isChild,
  disableReply,
}: Props) {
  const styles = useStyles();

  const {
    createScheduledActivityMessage,
  } = useScheduledActivityMessagesContext()

  const [formVisible, setFormVisible] = useState(false)
  const [showFullDate, setShowFullDate] = useState(false)

  const messagePadding = isChild ? 8 : 0
  const extraReplyPadding = 8
  const replyPadding = message.children.length > 0 ? 2 * extraReplyPadding : extraReplyPadding
  const createChildMessage = async (messageBody: string, parentMessageId?: string): Promise<boolean> => {

    const success = await createScheduledActivityMessage(messageBody, parentMessageId)
    if (success) {
      setFormVisible(false)
    }
    return success
  }

  const { pathname, hash } = useLocation()
  const messageRef = useRef<HTMLLIElement | null>(null)

  // if the url hash matches message id, scroll this message into view and focus it
  useEffect(() => {
    if (hash) {
      const messageId = hash.replace('#', '')
      if (messageId === message.id && messageRef.current) {
        const el = messageRef.current
        el.setAttribute("tabIndex", "-1")
        el.scrollIntoView({ block: "center" })
        el.focus()
      }
    }
  }, [pathname, hash])

  const messUser = new UserHelper(message.user)

  return (
    <>
      <ListItem className={styles.messageItem} alignItems="flex-start" sx={{ pl: messagePadding, pt: 2, pb: 1 }} ref={messageRef}>
        <ListItemAvatar>
          <UserAvatar userInfo={message.user} />
        </ListItemAvatar>
        <ListItemText className={styles.messageBody}>
          <Typography variant="subtitle1" color="textSecondary">
            {`${messUser.displayName()}`}
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            onClick={() => { setShowFullDate(!showFullDate) }}
            style={{ cursor: "pointer" }}
          >
            {tsFormatPastOnly(message.createdAt, showFullDate)}
          </Typography>
          <StyledMarkdown>
            {message.body}
          </StyledMarkdown>
        </ListItemText>
      </ListItem>
      {message.children.length > 0 && (
        <List sx={{ marginBottom: 0, padding: 0 }}
        >
          {message.children.map((message) => (
            <ScheduledActivityMessageDetail
              message={message}
              key={message.id}
              isChild={true}
            />
          ))}
        </List>
      )}
      {!message.parentMessageId && (
        <Grid
          container
          direction="column"
          sx={{ pl: replyPadding }}
        >
          {!disableReply &&
            <Grid item xs={12}>
              {!formVisible && (
                <Button sx={{ paddingLeft: 1, paddingTop: 0 }} onClick={() => setFormVisible(true)}>Reply</Button>
              )}
              {formVisible && (
                <ScheduledActivityMessageForm
                  label="Reply"
                  autoFocus={true}
                  parentMessageId={message.id}
                  createMessage={createChildMessage}
                />
              )}
            </Grid>
          }
        </Grid>
      )}
    </>
  )
}
